import { FC, MouseEvent, useContext, useState } from 'react';
import { Chips, LoadingSpinner } from '@medtronic/pecc-react-component-library-js';
import { Box, Button, Checkbox, Grid, ListItem, Menu, MenuItem, Typography } from '@mui/material';
import { format } from 'date-fns';
import { FsRawNode } from '~/api/types';
import {
  useDeleteFilesMutation,
  useDownloadFileMutation,
  useGetStorageQuery,
  useGetStorageStatsQuery,
  useRenameFileMutation,
} from '~/api/v2/hooks';
import { DeleteTypeEnum, ModalTypeEnum, SelectedFilesTable, Tooltip } from '~/components';
import { StorageContext } from '~/hooks';
import { DocumentIcon, VerticalDotsIcon } from '~/icons';
import { LinearProgressWithLabel } from '~/pages/DatasetDetailsPage/components';
import { ProgressStatus } from '~/pages/DatasetDetailsPage/types';
import { useAppDispatch } from '~/redux';
import { ModalTypesEnum, openModal } from '~/redux/slices/modalSlice';
import { DATE_FORMAT, formatBytes, getInnerPath } from '~/utils';
import {
  fileListItemCheckboxGridStyles,
  fileListItemContainerStyles,
  fileListItemDocumentIconStyles,
  fileListItemMenuElevation,
  fileListItemMenuItemStyles,
  fileListItemMoreButtonGridStyles,
  fileListItemMoreButtonStyles,
  fileListItemNameGridStyles,
  fileListItemStyles,
  fileListItemVerticalDotsIconStyles,
  fileNameStyles,
  filesListCheckboxStyles,
} from './styles';

interface FileListItemProps {
  file: FsRawNode;
  isChecked: boolean;
  handleToggle: (value: string) => void;
}

const FileListItem: FC<FileListItemProps> = ({ file, isChecked, handleToggle }) => {
  const dispatch = useAppDispatch();
  const { refetch: refetchStorage } = useGetStorageQuery({});
  const { refetch: refetchStats } = useGetStorageStatsQuery({});
  const [deleteFiles] = useDeleteFilesMutation({ fixedCacheKey: 'delete-file' });
  const [renameFile] = useRenameFileMutation({ fixedCacheKey: 'rename-file' });
  const [downloadFile, { isLoading: isDownloading }] = useDownloadFileMutation({});
  const innerFilePath = getInnerPath(file.path);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const storageContext = useContext(StorageContext);
  const uploadingFile = storageContext?.uploadingFiles?.get(file.name);

  const handleCloseMenu = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(null);
  };

  const handleDeleteFiles = async () => {
    await deleteFiles({ file_path_list: [file.path] });
    refetchStorage();
    refetchStats();
  };

  return (
    <>
      <ListItem sx={fileListItemStyles}>
        <Grid container sx={fileListItemContainerStyles}>
          <Grid size={{ xs: 1 }} sx={fileListItemCheckboxGridStyles}>
            <Checkbox
              onChange={() => handleToggle(file.name)}
              edge="start"
              checked={isChecked}
              sx={filesListCheckboxStyles}
              disabled={!!uploadingFile}
            />
          </Grid>
          <Grid size={{ xs: 6 }} sx={fileListItemNameGridStyles}>
            <DocumentIcon style={fileListItemDocumentIconStyles} fill="#0000008C" />
            <Tooltip title={file.name} sx={{ width: '100%', maxWidth: 'fit-content' }}>
              <Typography component="div" sx={fileNameStyles}>
                {file.name}
              </Typography>
            </Tooltip>
            {file.name.includes('.') && (
              <Chips
                chipsConfig={[
                  {
                    label: file.name.split('.').slice(-1)[0].toUpperCase(),
                  },
                ]}
              />
            )}
          </Grid>
          <Grid size={{ xs: 1.5 }}>
            <Typography variant="body2">{formatBytes(file.size!)}</Typography>
          </Grid>
          {uploadingFile ? (
            <Grid size={{ xs: 3 }}>
              <LinearProgressWithLabel
                value={uploadingFile?.status === ProgressStatus.RUNNING ? uploadingFile?.progress : 100}
                status={uploadingFile?.status || ProgressStatus.COMPLETED}
              />
            </Grid>
          ) : (
            <>
              <Grid size={{ xs: 2 }}>
                <Typography variant="body2">{format(new Date(file.last_modified), DATE_FORMAT)}</Typography>
              </Grid>
              <Grid size={{ xs: 1.5 }} sx={fileListItemMoreButtonGridStyles}>
                {isDownloading ? (
                  <Box mr={2}>
                    <LoadingSpinner spinnerSize="xs" />
                  </Box>
                ) : (
                  <Button
                    sx={fileListItemMoreButtonStyles}
                    endIcon={<VerticalDotsIcon style={fileListItemVerticalDotsIconStyles} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setAnchorEl(e.currentTarget);
                    }}
                  >
                    More
                  </Button>
                )}
              </Grid>
              <Menu
                elevation={fileListItemMenuElevation}
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
              >
                <MenuItem
                  sx={fileListItemMenuItemStyles}
                  onClick={(e) => {
                    dispatch(
                      openModal({
                        type: ModalTypesEnum.DELETE_CONFIRMATION,
                        payload: {
                          type: DeleteTypeEnum.FILES,
                          onDelete: handleDeleteFiles,
                          content: <SelectedFilesTable selectedFiles={[file]} />,
                        },
                      }),
                    );
                    handleCloseMenu(e);
                  }}
                >
                  Delete
                </MenuItem>
                <MenuItem
                  sx={fileListItemMenuItemStyles}
                  onClick={(e) => {
                    dispatch(
                      openModal({
                        type: ModalTypesEnum.FILE_SYSTEM_ACTION,
                        payload: {
                          type: ModalTypeEnum.RENAME_FILE,
                          onRename: renameFile,
                          filePath: innerFilePath,
                        },
                      }),
                    );
                    handleCloseMenu(e);
                  }}
                >
                  Rename
                </MenuItem>
                <MenuItem
                  sx={fileListItemMenuItemStyles}
                  onClick={(e) => {
                    downloadFile({ filePath: innerFilePath, fileName: file.name });
                    handleCloseMenu(e);
                  }}
                >
                  Download
                </MenuItem>
              </Menu>
            </>
          )}
        </Grid>
      </ListItem>
    </>
  );
};

export default FileListItem;
